import { useCallback } from 'react'

import { useApp } from '../../../app/AppContext.jsx'
import { useCatalog } from '../context/CatalogContext.jsx'

import { useProductStrings } from '../../../shared/i18n/useProductStrings.js'

import { useMobileNavDestinations } from '../../navigation/hooks/useMobileNavDestinations.js'
import { FETCH_STATUS } from '../../../shared/constants/fetchStatus.js'
import {
  MOBILE_DESTINATION_ID,
} from '../../navigation/utils/mobileNavDestinations.js'

import { CatalogFiltersSheet }  from './CatalogFilters/CatalogFiltersSheet.jsx'
import { CatalogSortRow }       from './CatalogBar/CatalogSortRow.jsx'

import { Button } from '../../../shared/ui/Button/Button.jsx'

// ── Main component ─────────────────────────────────────────────────────────────

/**
 * Catalog controller to be used in other domains mobile views. (HeroView, TiersView, FavoritesView)
 * Renders the CatalogFiltersSheet with trigger and Sorting Select inputs, as well as button to navigate to CatalogView destination. 
 * The product list/cards/tiles are not rendered here.
 */
export function CatalogMobileController() {
  const { 
    projectConfig,
    panelSettings, 
    windowData, 
    logoSrc, 
    isMobile, 
  } = useApp()
  const {
    catalogProducts,
    catalogCapabilities,
    productsStatus,
    productsError,
    filteredProducts,
    activeFiltersConfig,
    fullFiltersRange,
    defaultFiltersConfig,
    setActiveFiltersConfig,
    activeFiltersCount,
    resetFilters,
    restorePreReset,
    canRestorePreReset,
    isFiltersSheetOpen,
    setIsFiltersSheetOpen,
  } = useCatalog()
  const getProductCategoryString = useProductStrings()

  const {
    navigateToDestination,
  } = useMobileNavDestinations()

  const handleFilterChange = useCallback((id, value) => {
    setActiveFiltersConfig(prev => ({ ...prev, [id]: value }))
  }, [setActiveFiltersConfig])


  // ── Loading / error states ──────────────────────────────────────────────

  if (productsStatus === FETCH_STATUS.LOADING) {
    return <div>[CatalogView] Loading products…</div>
  }

  if (productsStatus === FETCH_STATUS.ERROR) {
    return (
      <div>
        [CatalogView] Products failed to load: {productsError?.message ?? 'unknown error'}
      </div>
    )
  }

  // ── Ready ───────────────────────────────────────────────────────────────

  if (!isMobile) return null // This controller is only for mobile views

  return (
    <div>
      {activeFiltersConfig && fullFiltersRange && (
        <CatalogFiltersSheet
          activeFiltersCount={activeFiltersCount}
          canRestorePreReset={canRestorePreReset}
          catalogFilters={catalogCapabilities?.catalogFilters}
          defaultFiltersConfig={defaultFiltersConfig}
          filteredCount={filteredProducts.length}
          filtersConfig={activeFiltersConfig}
          fullFiltersRange={fullFiltersRange}
          isOpen={isFiltersSheetOpen}
          onClose={() => setIsFiltersSheetOpen(false)}
          onFilterChange={handleFilterChange}
          onOpen={() => setIsFiltersSheetOpen(true)}
          onReset={resetFilters}
          onRestorePreReset={restorePreReset}
          totalCount={catalogProducts.length}
        />
      )}

      {/* ── Sort row ── */}
      <CatalogSortRow
        viewMode={null} //not to render the view mode toggle
      />

      <Button
        type='button'
        kind='wide'
        size='large'
        // className={styles.catalogViewBtn}
        onClick={() => navigateToDestination(MOBILE_DESTINATION_ID.CATALOG)}
      >
       {getProductCategoryString('open_catalog_view', 'default')}
      </Button>
    </div>
  )
}